import { useQuery } from '@tanstack/react-query';
import { Select, SelectProps } from 'antd';
import { useEffect, useState } from 'react';
import { getUsernamesByDepartmentDropdown } from '@martech-solution/utils/apis';

interface OptionType {
  label: React.ReactNode;
  value?: string | number | null;
}

interface OptionGroupType {
  label: React.ReactNode;
  departmentId?: number | undefined;
  options?: Omit<OptionType, 'children'>[];
}

type Props = Omit<SelectProps, 'value'> & {
  value?: (string | number)[];
  departmentId?: number | undefined;
  mode?: 'multiple' | 'tags' | undefined;
};

export default function UsernameMultipleSelect({
  value,
  departmentId,
  mode,
  onChange,
  style,
  allowClear = true,
  disabled
}: Props) {
  const [optionsGroup, setOptionsGroup] = useState<OptionGroupType[]>([]);

  const userNamesByDepartmentDropdownQuery = useQuery({
    queryKey: ['userNamesByDepartmentDropdown', departmentId],
    queryFn: () => getUsernamesByDepartmentDropdown(departmentId),
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (userNamesByDepartmentDropdownQuery.data?.data) {
      const newOptionsGroup: OptionGroupType[] = [];
      userNamesByDepartmentDropdownQuery.data?.data.forEach((item) => {
        const index = newOptionsGroup.findIndex((optionGroup) => optionGroup.label === item.department?.name);

        if (index === -1) {
          newOptionsGroup.push({
            label: item.department?.name,
            options: [
              {
                label: `${item.userName} - ${item.fullName}`,
                value: item.userName
              }
            ]
          });
        } else {
          newOptionsGroup[index].options?.push({
            label: `${item.userName} - ${item.fullName}`,
            value: item.userName
          });
        }
      });

      setOptionsGroup(newOptionsGroup);
    }
  }, [userNamesByDepartmentDropdownQuery.data?.data]);

  return (
    <Select
      mode={mode}
      allowClear={allowClear}
      disabled={disabled}
      value={userNamesByDepartmentDropdownQuery.isFetching ? undefined : value}
      style={style}
      onChange={onChange}
      options={optionsGroup}
      showSearch
      filterOption={(input, option) => (option?.label?.toString() ?? '').toLowerCase()?.includes(input.toLowerCase())}
      loading={userNamesByDepartmentDropdownQuery.isFetching}
    />
  );
}
